import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type TouchEvent,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import {
  OFFICIAL_LANDING_CSS_VARIABLES,
} from "../content/officialDesignTokens";
import {
  OFFICIAL_LANDING_CONTENT,
  type LandingCopy,
  type Language,
} from "../content/officialLandingContent";
import {
  buildLocalizedAuthPath,
  resolveAuthLanguage,
} from "../lib/authLanguage";
import { buildDemoModeSelectUrl } from "../lib/demoEntry";
import PremiumTimeline from "../components/PremiumTimeline";
import { AdaptiveText } from "../components/landing/AdaptiveText";
import { CookieConsentBanner } from "../components/landing/CookieConsentBanner";
import { debugLandingGa4, useLandingAnalytics } from "../components/landing/useLandingAnalytics";
import { useMetricoolTracking } from "../components/landing/useMetricoolTracking";
import { HeroPhoneShowcase } from "../components/landing/HeroPhoneShowcase";
import { HeroProductScene } from "../components/landing/hero/HeroProductScene";
import { AdaptiveJourneyGraphic } from "../components/landing/problem/AdaptiveJourneyGraphic";
import WeatherCycleOrb from "../components/landing/WeatherCycleOrb";
import { QuickStartTasksStep } from "../onboarding/steps/QuickStartTasksStep";
import { QUICK_START_TASKS } from "../onboarding/quickStart";
import { LabsWeeklyRhythmSystemSection } from "../components/labs/LabsWeeklyRhythmSystemSection";
import { buildOnboardingPath } from "../onboarding/i18n";
import { usePostLoginLanguage } from "../i18n/postLoginLanguage";
import {
  persistCookieConsentState,
  readCookieConsentState,
} from "../lib/cookieConsent";
import {
  SHOW_LANDING_PRICING,
} from "../config/releaseFlags";
import {
  type LandingThemeMode,
  getLandingThemeStyle,
  readLandingThemeMode,
} from "../lib/landingTheme";
import { useThemePreference } from "../theme/ThemePreferenceProvider";
import "./Landing.css";
import "./LandingThemeContrastPatch.css";

type ModeVisual = {
  avatarVideo: string;
  avatarImage: string;
  thumbImage: string;
  avatarAlt: string;
  avatarLabel: string;
};

const MODE_VISUALS: Record<
  Language,
  Record<"low" | "chill" | "flow" | "evolve", ModeVisual>
> = {
  en: {
    low: {
      avatarVideo: "/avatars/low-basic.mp4",
      avatarImage: "/LowMood.jpg",
      thumbImage: "/LowVertical.png",
      avatarAlt: "Red Cat avatar in Innerbloom.",
      avatarLabel: "Aligned with your energy",
    },
    chill: {
      avatarVideo: "/avatars/chill-basic.mp4",
      avatarImage: "/Chill-Mood.jpg",
      thumbImage: "/ChillVertical.png",
      avatarAlt: "Green Bear avatar in Innerbloom.",
      avatarLabel: "Aligned with your energy",
    },
    flow: {
      avatarVideo: "/avatars/flow-basic.mp4",
      avatarImage: "/FlowMood.jpg",
      thumbImage: "/FlowVertical.png",
      avatarAlt: "Blue Amphibian avatar in Innerbloom.",
      avatarLabel: "Aligned with your energy",
    },
    evolve: {
      avatarVideo: "/avatars/evolve-basic.mp4",
      avatarImage: "/Evolve-Mood.jpg",
      thumbImage: "/EvolveVertical.png",
      avatarAlt: "Violet Owl avatar in Innerbloom.",
      avatarLabel: "Aligned with your energy",
    },
  },
  es: {
    low: {
      avatarVideo: "/avatars/low-basic.mp4",
      avatarImage: "/LowMood.jpg",
      thumbImage: "/LowVertical.png",
      avatarAlt: "Avatar Red Cat dentro de Innerbloom.",
      avatarLabel: "Alineado a tu energía",
    },
    chill: {
      avatarVideo: "/avatars/chill-basic.mp4",
      avatarImage: "/Chill-Mood.jpg",
      thumbImage: "/ChillVertical.png",
      avatarAlt: "Avatar Green Bear dentro de Innerbloom.",
      avatarLabel: "Alineado a tu energía",
    },
    flow: {
      avatarVideo: "/avatars/flow-basic.mp4",
      avatarImage: "/FlowMood.jpg",
      thumbImage: "/FlowVertical.png",
      avatarAlt: "Avatar Blue Amphibian dentro de Innerbloom.",
      avatarLabel: "Alineado a tu energía",
    },
    evolve: {
      avatarVideo: "/avatars/evolve-basic.mp4",
      avatarImage: "/Evolve-Mood.jpg",
      thumbImage: "/EvolveVertical.png",
      avatarAlt: "Avatar Violet Owl dentro de Innerbloom.",
      avatarLabel: "Alineado a tu energía",
    },
  },
};

const buttonBaseClasses =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full px-6 py-3 font-display text-sm font-semibold tracking-tight transition duration-150 ease-out active:translate-y-[1px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white";

const buttonVariants = {
  primary: "ib-primary-button",
  ghost: `${buttonBaseClasses} border border-transparent bg-transparent text-text-subtle hover:bg-white/10 hover:text-white`,
};

const buttonClasses = (variant: keyof typeof buttonVariants = "primary") =>
  buttonVariants[variant];

const PILLAR_EXAMPLES_LABEL: Record<Language, string> = {
  es: "Tareas sugeridas:",
  en: "Suggested tasks:",
};

const V2_METHOD_VISUALS = [
  [
    "/landing-v2/method/step-01-onboarding-context.jpg",
    "/landing-v2/method/step-01-real-life-board.jpg",
  ],
  [
    "/landing-v2/method/step-02-rhythm-avatar-split.jpg",
    "/landing-v2/method/step-02-rhythm-control.jpg",
  ],
  [
    "/landing-v2/method/step-03-action-cards.jpg",
    "/landing-v2/method/step-03-progress-phone.jpg",
  ],
  [
    "/landing-v2/method/step-04-recalibration-dashboard.jpg",
    "/landing-v2/method/step-04-adjusted-plan.jpg",
  ],
] as const;

function renderMultilineText(text: string) {
  return text.split("\n").map((line, index) => (
    <Fragment key={`${line}-${index}`}>
      {index > 0 ? <br /> : null}
      {line}
    </Fragment>
  ));
}

function splitPillarCopy(copy: string, language: Language) {
  const examplesLabel = PILLAR_EXAMPLES_LABEL[language];
  const [definitionPart, examplesPart] = copy.split(examplesLabel);
  const definition = definitionPart?.trim() ?? copy;
  const examples = (examplesPart ?? "")
    .split("•")
    .map((item) => item.trim())
    .filter(Boolean);

  return { definition, examples };
}

const DISCORD_URL = "https://discord.gg/Y4ZaqDZCZ3";

const DISCORD_CTA_COPY: Record<Language, { lead: string; link: string }> = {
  en: {
    lead: "Got questions or feedback?",
    link: "Join us on Discord.",
  },
  es: {
    lead: "¿Tienes preguntas o feedback?",
    link: "Únete a nuestro Discord.",
  },
};

function BackToTopButton({ language }: { language: Language }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const landingScrollContainer = document.querySelector<HTMLElement>(".landing");
    const handleScroll = () => {
      setIsVisible((landingScrollContainer?.scrollTop ?? window.scrollY) > 500);
    };

    handleScroll();
    const scrollTarget: HTMLElement | Window = landingScrollContainer ?? window;
    scrollTarget.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollTarget.removeEventListener("scroll", handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <button
      type="button"
      className="landing-back-to-top"
      aria-label={language === "es" ? "Volver arriba" : "Back to top"}
      onClick={() => {
        const landingScrollContainer = document.querySelector<HTMLElement>(".landing");
        if (landingScrollContainer && landingScrollContainer.scrollHeight > landingScrollContainer.clientHeight) {
          landingScrollContainer.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }

        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
    >
      <span aria-hidden="true">↑</span>
      <span>Top</span>
    </button>
  );
}

const EMOTION_HEATMAP_ROWS: Array<
  Array<"calm" | "happy" | "focus" | "stress" | "neutral">
> = [
  [
    "calm",
    "happy",
    "calm",
    "happy",
    "neutral",
    "calm",
    "focus",
    "calm",
    "happy",
    "calm",
    "neutral",
    "focus",
    "calm",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
  ],
  [
    "calm",
    "focus",
    "calm",
    "neutral",
    "happy",
    "neutral",
    "calm",
    "focus",
    "calm",
    "happy",
    "focus",
    "stress",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
  ],
  [
    "calm",
    "focus",
    "neutral",
    "happy",
    "happy",
    "neutral",
    "focus",
    "calm",
    "focus",
    "calm",
    "focus",
    "stress",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
  ],
  [
    "calm",
    "focus",
    "neutral",
    "focus",
    "neutral",
    "neutral",
    "focus",
    "focus",
    "focus",
    "focus",
    "calm",
    "stress",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
  ],
  [
    "focus",
    "neutral",
    "neutral",
    "neutral",
    "calm",
    "happy",
    "focus",
    "neutral",
    "focus",
    "focus",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
  ],
  [
    "focus",
    "calm",
    "neutral",
    "calm",
    "focus",
    "neutral",
    "focus",
    "happy",
    "focus",
    "calm",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
  ],
  [
    "calm",
    "calm",
    "neutral",
    "neutral",
    "focus",
    "focus",
    "happy",
    "focus",
    "calm",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
    "neutral",
  ],
];

function LanguageDropdown({
  value,
  onChange,
}: {
  value: Language;
  onChange: (language: Language) => void;
}) {
  const options: { code: Language; label: string }[] = [
    { code: "en", label: "EN" },
    { code: "es", label: "ES" },
  ];

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentOption =
    options.find((option) => option.code === value) ?? options[0];

  function handleSelect(language: Language) {
    onChange(language);
    setIsOpen(false);
  }

  return (
    <div
      ref={dropdownRef}
      className="lang-toggle"
      role="group"
      aria-label="Language selector"
    >
      <button
        type="button"
        className="lang-button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="lang-button-label">{currentOption.label}</span>
        <span className="lang-caret" aria-hidden>
          ▾
        </span>
      </button>

      <div className="lang-menu" role="listbox" hidden={!isOpen}>
        {options.map((option) => (
          <button
            key={option.code}
            type="button"
            role="option"
            aria-selected={value === option.code}
            className={value === option.code ? "active" : ""}
            data-analytics-event="landing_language_changed"
            data-analytics-value={option.code}
            onClick={() => handleSelect(option.code)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

type LandingPageVariant = "default" | "v2Narrative" | "v3Conversion";

type LandingPageProps = {
  content?: Record<Language, LandingCopy>;
  variant?: LandingPageVariant;
};

function LandingNarrativeMethod({
  how,
  visuals,
}: {
  how: LandingCopy["how"];
  visuals: readonly (readonly string[])[];
}) {
  return (
    <div className="v2-method-shell">
      <div className="v2-method-heading">
        <p className="how-kicker">{how.kicker}</p>
        <AdaptiveText as="h2">{how.title}</AdaptiveText>
        <AdaptiveText as="p" className="section-sub v2-method-intro">
          {how.intro}
        </AdaptiveText>
      </div>

      <div className="v2-method-steps">
        {how.steps.map((step, index) => {
          const isVisualFirst = index % 2 === 1;
          const visualSources = visuals[index] ?? visuals[0];
          const copyBlock = (
            <div className="v2-method-step-copy">
              <span className="v2-method-step-badge">{step.badge}</span>
              <AdaptiveText as="h3" className="v2-method-step-title">
                {step.title}
              </AdaptiveText>
              <AdaptiveText as="p" className="v2-method-step-description">
                {step.bullets[0]}
              </AdaptiveText>
            </div>
          );
          const visualBlock = (
            <div className="v2-method-visual v2-method-visual--image" aria-hidden>
              {visualSources.map((src, visualIndex) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  className={`v2-method-visual-image ${visualIndex === 0 ? "is-primary" : "is-secondary"}`}
                  loading="lazy"
                  decoding="async"
                />
              ))}
            </div>
          );

          return (
            <article
              className={`v2-method-step ${isVisualFirst ? "v2-method-step--visual-first" : ""}`}
              key={step.badge}
            >
              {isVisualFirst ? (
                <>
                  {visualBlock}
                  {copyBlock}
                </>
              ) : (
                <>
                  {copyBlock}
                  {visualBlock}
                </>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

export function LandingV3MethodVisual({
  index,
  language,
  logrosCycleMs,
  nativePreview = false,
}: {
  index: number;
  language: Language;
  logrosCycleMs?: number;
  nativePreview?: boolean;
}) {
  const [animatedMinutes, setAnimatedMinutes] = useState("15");

  useEffect(() => {
    if (index !== 0) return undefined;

    const values = ["1", "15", "15", "1"];
    let frame = 0;
    const interval = window.setInterval(() => {
      frame = (frame + 1) % values.length;
      setAnimatedMinutes(values[frame]);
    }, 760);

    return () => window.clearInterval(interval);
  }, [index]);

  if (index === 0) {
    const tasks = QUICK_START_TASKS[language].Body.slice(0, 4).map((task) => {
      if (task.id === "ENERGIA") return { ...task, inputAfter: language === "es" ? "min" : "min" };
      if (task.id === "NUTRICION") return { ...task, text: language === "es" ? "comidas equilibradas al día" : "balanced meals a day" };
      return task;
    });
    return (
      <div className="v3-step-visual v3-method-product-visual v3-method-quickstart" aria-hidden>
        <div className="v3-method-quickstart__scene">
          <div className="v3-rhythm-preview v3-rhythm-preview--native">
            <p className="v3-quickstart-phase-title v3-quickstart-phase-title--rhythm">
              {language === "es" ? "Elegí tu ritmo" : "Choose your rhythm"}
            </p>
            <LandingV3RhythmPickerPreview language={language} />
          </div>
          <div className="v3-quickstart-tasks-preview onboarding2-premium-root">
            <p className="v3-quickstart-phase-title v3-quickstart-phase-title--tasks">
              {language === "es" ? "Customizá tus tareas" : "Customize your tasks"}
            </p>
            <QuickStartTasksStep
              language={language}
              pillar="Body"
              tasks={tasks}
              selectedIds={tasks.slice(0, 3).map((task) => task.id)}
              inputValues={{ "Body-ENERGIA": animatedMinutes, "Body-NUTRICION": "3", "Body-SUENO": "7" }}
              minimum={1}
              gameMode="FLOW"
              balancedBonusActive={false}
              variant="onboarding2"
              onToggleTask={() => undefined}
              onInputChange={() => undefined}
              onBack={() => undefined}
              onConfirm={() => undefined}
            />
          </div>
        </div>
      </div>
    );
  }

  if (index === 1) {
    return (
      <div className="v3-step-visual v3-method-product-visual v3-method-streaks" data-light-scope="dashboard-v3" aria-hidden>
        <LandingV3MobileTasksVisual />
      </div>
    );
  }

  if (index === 2) {
    return (
      <div className="v3-step-visual v3-method-product-visual v3-method-adjustment" data-light-scope="dashboard-v3" aria-hidden>
        <LandingV3AdjustmentVisual language={language} />
      </div>
    );
  }

  return (
    <div className="v3-step-visual v3-method-product-visual v3-method-logros" data-light-scope="dashboard-v3" aria-hidden>
      <LandingV3LogrosVisual language={language} cycleMs={logrosCycleMs} />
    </div>
  );
}

const LANDING_V3_RHYTHMS = [
  { key: "low", label: "LOW", days: 1 },
  { key: "chill", label: "CHILL", days: 2 },
  { key: "flow", label: "FLOW", days: 3 },
  { key: "evolve", label: "EVOLVE", days: 4 },
] as const;

function LandingV3RhythmPickerPreview({ language }: { language: Language }) {
  const rhythmCopy = {
    low: language === "es" ? "Base suave" : "Soft base",
    chill: language === "es" ? "Constancia simple" : "Simple consistency",
    flow: language === "es" ? "Ritmo sostenido" : "Sustained rhythm",
    evolve: language === "es" ? "Mayor intensidad" : "Higher intensity",
    days: language === "es" ? "días/sem" : "days/week",
  };

  return (
    <div className="v3-rhythm-sheet-preview" role="img" aria-label={language === "es" ? "Selector de ritmo con Flow seleccionado" : "Rhythm selector with Flow selected"}>
      <div className="v3-rhythm-sheet-options">
        {LANDING_V3_RHYTHMS.map((rhythm) => {
          const isFlow = rhythm.key === "flow";
          return (
            <article className={`v3-rhythm-sheet-option ${isFlow ? "is-flow" : ""}`} key={rhythm.key}>
              <div className="v3-rhythm-sheet-option__top">
                <div>
                  <strong>{rhythm.label}</strong>
                  <span>{rhythmCopy[rhythm.key]}</span>
                </div>
                <div>
                  <b>{rhythm.days}</b>
                  <em>{rhythmCopy.days}</em>
                </div>
              </div>
              <div className="v3-rhythm-segments" aria-hidden="true">
                {Array.from({ length: 4 }, (_, index) => (
                  <i className={index < rhythm.days ? "is-filled" : ""} key={index} />
                ))}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

const LANDING_V3_TASK_ROWS = [
  { icon: "↻", title: "20` Sin pantallas ...", trait: "Recuperación", difficulty: "Difícil", difficultyClass: "is-hard", progress: "0/3", done: 0, streak: null, weeks: [0, 0, 0, 0, 0] },
  { icon: "♢", title: "2L de agua", trait: "Hidratación", difficulty: "Fácil", difficultyClass: "is-easy", progress: "2/3", done: 2, streak: null, weeks: [1, 1, 2, 0, 0] },
  { icon: "🔥", title: "Ayuno hasta las 14hs", trait: "Nutrición", difficulty: "Fácil", difficultyClass: "is-easy", progress: "2/3", done: 2, streak: "2d", weeks: [1, 0, 2, 0, 0] },
  { icon: "♧", title: "Cena antes de las 21hs", trait: "Nutrición", difficulty: "Fácil", difficultyClass: "is-easy", progress: "2/3", done: 2, streak: null, weeks: [1, 1, 2, 0, 0] },
  { icon: "🔥", title: "Dormir 8hs", trait: "Sueño", difficulty: "Media", difficultyClass: "is-medium", progress: "3/3", done: 3, streak: "4d", weeks: [1, 1, 1, 0, 0] },
  { icon: "⇄", title: "Hacer ejercicios d...", trait: "Movilidad", difficulty: "Fácil", difficultyClass: "is-easy", progress: "1/3", done: 1, streak: null, weeks: [1, 1, 2, 0, 0] },
] as const;

function LandingV3MobileTasksVisual() {
  return (
    <div className="ib20-showcase ib20-showcase--tasks">
      <div className="ib20-showcase-glow" />
      <div className="ib20-tasks-panel">
        <div className="ib20-showcase-topline">
          <span>Tareas</span>
          <b>Cuerpo</b>
        </div>
        <div className="ib20-task-head">
          <span>Tarea</span>
          <span>Semanas</span>
          <span>Progreso</span>
        </div>
        <div className="ib20-task-list">
          {LANDING_V3_TASK_ROWS.slice(1, 5).map((task, index) => (
            <article className="ib20-task-row" key={task.title} style={{ "--delay": `${index * 120}ms` } as CSSProperties}>
              <div className={`ib20-task-icon ${task.streak ? "has-streak" : ""}`}>
                <b>{task.icon}</b>
                {task.streak ? <small>{task.streak}</small> : null}
              </div>
              <div className="ib20-task-copy">
                <strong>{task.title}</strong>
                <p>
                  {task.trait}
                  <i />
                  <span className={task.difficultyClass}>{task.difficulty}</span>
                  <em>✦</em>
                </p>
              </div>
              <div className="ib20-task-weeks">
                {task.weeks.map((state, weekIndex) => (
                  <span className={state === 1 ? "is-green" : state === 2 ? "is-gold" : ""} key={`${task.title}-${weekIndex}`} />
                ))}
                {task.weeks.map((_, weekIndex) => (
                  <i key={`${task.title}-label-${weekIndex}`}>S{weekIndex + 1}</i>
                ))}
              </div>
              <div className="ib20-task-ring" style={{ "--progress": `${Math.min(100, (task.done / 3) * 100)}%` } as CSSProperties}>
                <b>{task.progress}</b>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

function LandingV3AdjustmentVisual({ language }: { language: Language }) {
  const isSpanish = language === "es";
  const demoRef = useRef<HTMLDivElement | null>(null);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    const node = demoRef.current;
    if (!node || hasEntered) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setHasEntered(true);
          observer.disconnect();
        }
      },
      { threshold: 0.42 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasEntered]);

  return (
    <div className={`ib20-showcase ib20-showcase--detail ${hasEntered ? "is-visible" : ""}`} ref={demoRef}>
      <div className="ib20-showcase-glow" />
      <div className="ib20-detail-shell">
        <div className="ib20-detail-nav">
          <span aria-hidden>‹</span>
          <p>{isSpanish ? "Detalle de tarea" : "Task detail"}</p>
          <span aria-hidden />
        </div>

        <section className="ib20-detail-head">
          <div className="ib20-detail-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" focusable="false">
              <path d="M12 3.4 18.8 6v5.2c0 4.2-2.6 7.4-6.8 8.8-4.2-1.4-6.8-4.6-6.8-8.8V6L12 3.4Z" />
              <path d="m8.7 12 2.1 2.1 4.6-5" />
            </svg>
          </div>
          <div className="ib20-detail-copy">
            <h4>{isSpanish ? "Revisar gastos del día" : "Review daily expenses"}</h4>
            <div className="ib20-detail-chips">
              <span className="ib20-detail-chip ib20-detail-chip--trait">
                {isSpanish ? "Autocontrol" : "Self-control"}
              </span>
              <span className="ib20-detail-chip ib20-detail-chip--difficulty">{isSpanish ? "Fácil" : "Easy"}</span>
              <span className="ib20-detail-chip ib20-detail-chip--down">
                <i aria-hidden>↓</i>
                <b>{isSpanish ? "Bajó la dificultad" : "Difficulty lowered"}</b>
              </span>
            </div>
          </div>
          <span className="ib20-detail-chevron" aria-hidden>›</span>
        </section>

        <section className="ib20-habit-dev">
          <p>{isSpanish ? "Desarrollo del hábito" : "Habit development"}</p>
          <div className="ib20-habit-body">
            <div className="ib20-habit-ring" style={{ "--score-target": "226.8deg", "--score-color": "#f5c55a" } as CSSProperties}>
              <strong>63</strong>
              <span>Score</span>
            </div>
            <div className="ib20-habit-content">
              <span className="ib20-habit-status">{isSpanish ? "Hábito en construcción" : "Habit in progress"}</span>
              <p className="ib20-habit-insight">{isSpanish ? "Estás avanzando de forma constante." : "You're progressing steadily."}</p>
              <div className="ib20-habit-divider" />
              <div className="ib20-habit-ranges">
                <span className="is-fragile">{isSpanish ? "Frágil <50" : "Fragile <50"}</span>
                <i />
                <span className="is-building">50-79</span>
                <i />
                <span className="is-strong">{isSpanish ? "Fuerte ≥80" : "Strong ≥80"}</span>
              </div>
              <div className="ib20-habit-window-title">
                <i className="is-spacer" />
                <span>{isSpanish ? "Ventana activa" : "Active window"}</span>
                <i />
              </div>
              <div className="ib20-habit-months">
                <span className="is-outside-window"><i className="is-building">53%</i><b>mar</b></span>
                <span className="is-active-window"><i className="is-building">72%</i><b>abr</b></span>
                <span><i className="is-fragile">38%</i><b>may</b></span>
                <span className="is-projected"><i className="is-strong">96%</i><b>jun</b><small>{isSpanish ? "proyectado" : "projected"}</small></span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function LandingV3MobileLogrosVisual() {
  return (
    <div className="ib20-showcase ib20-showcase--logros">
      <div className="ib20-showcase-glow" />
      <div className="ib20-logros-panel">
        <div className="ib20-showcase-topline">
          <span>Logros</span>
          <b>Hábitos logrados</b>
        </div>
        <div className="ib20-logros-carousel">
          <article className="ib20-logros-side ib20-logros-side--left" />
          <article className="ib20-logros-card">
            <div className="ib20-logros-card-inner">
              <div className="ib20-logros-face ib20-logros-front">
                <img src="/sellos/body/sello_body_hydration.png" alt="" />
                <strong>2L de agua</strong>
                <p>Hidratación <span>·</span> <b>Logrado</b></p>
                <button type="button">↟ Compartir</button>
              </div>
              <div className="ib20-logros-face ib20-logros-back">
                <p>LOGRO DESBLOQUEADO</p>
                <strong>2L de agua</strong>
                <span>Hidratación</span>
                <dl>
                  <div><dt>Logrado</dt><dd>2026-04-02</dd></div>
                  <div><dt>GP antes</dt><dd>158 GP</dd></div>
                  <div><dt>Mantener</dt><dd><i /></dd></div>
                </dl>
                <small>Toca para volver</small>
              </div>
            </div>
          </article>
          <article className="ib20-logros-side ib20-logros-side--right" />
        </div>
        <div className="ib20-logros-count">2 / 11</div>
        <div className="ib20-logros-dots">
          {Array.from({ length: 7 }, (_, index) => (
            <span className={index === 1 ? "is-active" : ""} key={index} />
          ))}
        </div>
      </div>
    </div>
  );
}

type LandingV3LogrosCard = {
  achievedAt?: string;
  flip?: boolean;
  gpBefore?: string;
  development?: {
    months: Array<{ month: string; percent: number; projected?: boolean }>;
    score: number;
    status: string;
  };
  locked?: boolean;
  name: string;
  src: string;
  trait: string;
};

type LandingV3LogrosSet = {
  cards: LandingV3LogrosCard[];
  icon: string;
  label: string;
};

const V3_LOGROS_SETS: Record<"BODY" | "MIND" | "SOUL", LandingV3LogrosSet> = {
  BODY: {
    label: "Body",
    icon: "",
    cards: [
      { name: "2L de agua", trait: "Hidratación", src: "/sellos/body/sello_body_hydration.png", flip: true, achievedAt: "2026-03-18", gpBefore: "1.240 GP" },
      { name: "10.000 pasos", trait: "Movilidad", src: "/sellos/body/sello_body_hydration.png", locked: true, development: { score: 63, status: "Hábito en construcción", months: [{ month: "mar", percent: 53 }, { month: "abr", percent: 72 }, { month: "may", percent: 38 }, { month: "jun", percent: 96, projected: true }] } },
      { name: "Dormir 8hs", trait: "Sueño", src: "/sellos/body/sello_body_nutrition.png" },
      { name: "Gym", trait: "Fuerza", src: "/sellos/body/sello_body_nutrition.png", locked: true, development: { score: 27, status: "Hábito frágil", months: [{ month: "mar", percent: 8 }, { month: "abr", percent: 9 }, { month: "may", percent: 8 }, { month: "jun", percent: 65, projected: true }] } },
    ],
  },
  MIND: {
    label: "Mind",
    icon: "",
    cards: [
      { name: "Inglés", trait: "Focus", src: "/sellos/mind/sello_mind_focus.png", flip: true, achievedAt: "2026-04-02", gpBefore: "980 GP" },
      { name: "Leer 20m", trait: "Claridad", src: "/sellos/mind/sello_mind_focus.png", locked: true, development: { score: 27, status: "Hábito frágil", months: [{ month: "mar", percent: 8 }, { month: "abr", percent: 9 }, { month: "may", percent: 8 }, { month: "jun", percent: 65, projected: true }] } },
      { name: "Lectura 20m", trait: "Claridad", src: "/sellos/mind/sello_mind_focus.png" },
      { name: "Journaling", trait: "Memoria", src: "/sellos/mind/sello_mind_focus.png", locked: true, development: { score: 63, status: "Hábito en construcción", months: [{ month: "mar", percent: 53 }, { month: "abr", percent: 72 }, { month: "may", percent: 38 }, { month: "jun", percent: 96, projected: true }] } },
    ],
  },
  SOUL: {
    label: "Soul",
    icon: "",
    cards: [
      { name: "Llamar familia", trait: "Conexión", src: "/sellos/soul/soul_connection_transparent.png", flip: true, achievedAt: "2026-04-16", gpBefore: "760 GP" },
      { name: "Pausa diaria", trait: "Presencia", src: "/sellos/soul/soul_connection_transparent.png", locked: true, development: { score: 63, status: "Hábito en construcción", months: [{ month: "mar", percent: 53 }, { month: "abr", percent: 72 }, { month: "may", percent: 38 }, { month: "jun", percent: 96, projected: true }] } },
      { name: "Salir sin móvil", trait: "Calma", src: "/sellos/soul/soul_connection_transparent.png" },
      { name: "Juego", trait: "Play", src: "/sellos/soul/soul_connection_transparent.png", locked: true, development: { score: 27, status: "Hábito frágil", months: [{ month: "mar", percent: 8 }, { month: "abr", percent: 9 }, { month: "may", percent: 8 }, { month: "jun", percent: 65, projected: true }] } },
    ],
  },
} as const;

function LandingV3LogrosVisual({ language, cycleMs = 5200 }: { language: Language; cycleMs?: number }) {
  const [active, setActive] = useState<{ pillar: keyof typeof V3_LOGROS_SETS; index: number }>({
    pillar: "BODY",
    index: 0,
  });

  useEffect(() => {
    const cycle: Array<{ pillar: keyof typeof V3_LOGROS_SETS; index: number }> = [
      { pillar: "BODY", index: 0 },
      { pillar: "BODY", index: 1 },
      { pillar: "MIND", index: 0 },
      { pillar: "MIND", index: 1 },
      { pillar: "SOUL", index: 0 },
      { pillar: "SOUL", index: 1 },
    ];
    let frame = 0;

    const interval = window.setInterval(() => {
      frame = (frame + 1) % cycle.length;
      setActive(cycle[frame]);
    }, cycleMs);

    return () => window.clearInterval(interval);
  }, [cycleMs]);

  const current = V3_LOGROS_SETS[active.pillar];
  const isSpanish = language === "es";
  return (
    <div className="v3-method-logros__scene" style={{ "--active-card": active.index } as CSSProperties}>
      <p className="v3-logros-shelf-title">{isSpanish ? "Estante de hábitos" : "Habit shelf"}</p>
      <div className="v3-logros-tabs">
        {(Object.keys(V3_LOGROS_SETS) as Array<keyof typeof V3_LOGROS_SETS>).map((pillar) => (
          <span className={pillar === active.pillar ? "is-active" : ""} key={pillar}>
            {isSpanish
              ? pillar === "BODY"
                ? "Cuerpo"
                : pillar === "MIND"
                  ? "Mente"
                  : "Alma"
              : V3_LOGROS_SETS[pillar].label}
          </span>
        ))}
      </div>

      <div className="v3-logros-viewport">
        <div className="v3-logros-track" key={active.pillar}>
          {current.cards.map((card, index) => (
            <article className={`v3-logros-card ${index === active.index ? "is-center" : ""} ${card.locked ? "is-locked" : ""} ${card.flip || card.locked ? "is-flippable" : ""}`} key={`${active.pillar}-${card.name}`}>
              <div className="v3-logros-card__inner">
                <div className="v3-logros-card__face v3-logros-card__front">
                  <img src={card.src} alt="" />
                  <strong>{card.name}</strong>
                  <p>
                    <span>{card.trait}</span>
                    <i aria-hidden>·</i>
                    <b>{card.locked ? (isSpanish ? "Bloqueado" : "Locked") : (isSpanish ? "Logrado" : "Achieved")}</b>
                  </p>
                  {!card.locked ? <span className="v3-logros-share">↟ {isSpanish ? "Compartir" : "Share"}</span> : null}
                  <span className="v3-logros-corner" aria-hidden>↘</span>
                </div>
                {card.flip ? (
                  <div className="v3-logros-card__face v3-logros-card__back">
                    <span>{isSpanish ? "Logro desbloqueado" : "Achievement unlocked"}</span>
                    <strong>{card.name}</strong>
                    <p>{card.trait}</p>
                    <dl>
                      <div>
                        <dt>{isSpanish ? "Logrado" : "Achieved"}</dt>
                        <dd>{card.achievedAt}</dd>
                      </div>
                      <div>
                        <dt>{isSpanish ? "GP antes" : "GP before"}</dt>
                        <dd>{card.gpBefore}</dd>
                      </div>
                      <div className="v3-logros-keep-row">
                        <dt>{isSpanish ? "Mantener activo" : "Keep active"}</dt>
                        <dd>
                          <span className="v3-logros-keep-toggle" aria-hidden>
                            <i />
                          </span>
                        </dd>
                      </div>
                    </dl>
                  </div>
                ) : card.locked && card.development ? (
                  <div className="v3-logros-card__face v3-logros-card__back v3-logros-card__back--development">
                    <p>{isSpanish ? "Desarrollo del hábito" : "Habit development"}</p>
                    <span className={`v3-logros-dev-status ${card.development.score < 50 ? "is-fragile" : "is-building"}`}>
                      {card.development.status}
                    </span>
                    <div className="v3-logros-dev-ring" style={{ "--score-target": `${card.development.score * 3.6}deg`, "--score-color": card.development.score < 50 ? "#ff6670" : "#f5c55a" } as CSSProperties}>
                      <strong>{card.development.score}</strong>
                      <span>Score</span>
                    </div>
                    <div className="v3-logros-dev-ranges">
                      <span>Frágil &lt;50</span><i /> <span>50-79</span><i /> <span>Fuerte ≥80</span>
                    </div>
                    <div className="v3-logros-dev-window">
                      <b>{isSpanish ? "Ventana activa" : "Active window"}</b>
                      <div>
                        {card.development.months.map((month) => (
                          <span className={month.projected ? "is-projected" : ""} key={`${card.name}-${month.month}`}>
                            <i className={month.percent < 50 ? "is-fragile" : month.percent < 80 ? "is-building" : "is-strong"}>{month.percent}%</i>
                            <em>{month.month}</em>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
      <div className="v3-logros-count">{active.index + 1} / {current.cards.length}</div>
    </div>
  );
}

function LandingV3ConversionMethod({ how, language }: { how: LandingCopy["how"]; language: Language }) {
  return (
    <div className="v2-method-shell v3-method-shell">
      <div className="v2-method-heading v3-method-snap-panel v3-method-snap-panel--intro">
        <p className="how-kicker">{how.kicker}</p>
        <AdaptiveText as="h2">{how.title}</AdaptiveText>
        <AdaptiveText as="p" className="section-sub v2-method-intro">
          {how.intro}
        </AdaptiveText>
      </div>

      <div className="v3-method-card-grid">
        {how.steps.map((step, index) => {
          return (
            <article
              className={`v3-method-card v3-method-snap-panel v3-method-card--${index % 2 === 0 ? "visual-right" : "visual-left"}`}
              key={step.badge}
              style={{ "--delay": `${index * 80}ms` } as CSSProperties}
            >
              <LandingV3MethodVisual index={index} language={language} />
              <div className="v3-method-card-copy">
                <span className="v2-method-step-badge">{step.badge}</span>
                <AdaptiveText as="h3" className="v2-method-step-title">
                  {step.title}
                </AdaptiveText>
                <AdaptiveText as="p" className="v2-method-step-description">
                  {step.bullets[0]}
                </AdaptiveText>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function LandingV3OnboardingCta({
  copy,
  dashboardHref,
  href,
  isSignedIn,
}: {
  copy: LandingCopy;
  dashboardHref: string;
  href: string;
  isSignedIn: boolean;
}) {
  return (
    <section className="v3-onboarding-cta section-pad reveal-on-scroll">
      <div className="container narrow">
        <div className="v3-onboarding-cta__shell">
          <div className="v3-onboarding-cta__copy">
            <AdaptiveText as="h2">{copy.next.title}</AdaptiveText>
            <AdaptiveText as="p" className="section-sub">
              {copy.next.intro}
            </AdaptiveText>
          </div>
          <div className="v3-onboarding-cta__action">
            <Link
              className={buttonClasses()}
              data-analytics-cta={isSignedIn ? "dashboard" : "start_journey"}
              data-analytics-location="method"
              to={isSignedIn ? dashboardHref : href}
            >
              {isSignedIn ? copy.auth.dashboard : copy.auth.startJourney}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage({
  content = OFFICIAL_LANDING_CONTENT,
  variant = "default",
}: LandingPageProps) {
  const { userId } = useAuth();
  const { setManualLanguage, syncLocaleLanguage } = usePostLoginLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const isSignedIn = Boolean(userId);
  const [language, setLanguage] = useState<Language>(() =>
    typeof window !== "undefined"
      ? resolveAuthLanguage(window.location.search)
      : "es",
  );
  const { theme, setPreference } = useThemePreference();
  const themeMode: LandingThemeMode = theme;
  const copy = content[language];
  const isV2Narrative = variant === "v2Narrative";
  const isV3Conversion = variant === "v3Conversion";
  const isNarrativeVariant = isV2Narrative || isV3Conversion;
  const isOfficialDefault = variant === "default";
  const visibleNavLinks = copy.navLinks.filter(
    (link) => !/^\/demo$/i.test(link.href) && !/^#?demo$/i.test(link.href),
  );
  const onboardingHref = isV3Conversion
    ? `/onboarding2?lang=${language}`
    : buildOnboardingPath(language);
  const loginHref = buildLocalizedAuthPath(isV3Conversion ? "/login2" : "/login", language);
  const signUpHref = buildLocalizedAuthPath(isV3Conversion ? "/sign-up2" : "/sign-up", language);
  const dashboardHref = isV3Conversion ? "/innerbloom2/dashboard" : "/dashboard";
  const landingStyle = {
    ...(OFFICIAL_LANDING_CSS_VARIABLES as CSSProperties),
    ...getLandingThemeStyle(themeMode),
  } as CSSProperties;
  const [activeSlide, setActiveSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [activeModeIndex, setActiveModeIndex] = useState(0);
  const [isModesInView, setIsModesInView] = useState(false);
  const [hasModeInteracted, setHasModeInteracted] = useState(false);
  const initialCookieConsentStateRef = useRef(readCookieConsentState());
  const [analyticsConsent, setAnalyticsConsent] = useState(
    () => initialCookieConsentStateRef.current.analytics,
  );
  const [isCookiePanelOpen, setIsCookiePanelOpen] = useState(
    () => initialCookieConsentStateRef.current.analytics === "unset",
  );
  const modesSectionRef = useRef<HTMLElement | null>(null);
  const landingRootRef = useRef<HTMLDivElement | null>(null);
  const modeThumbTouchStartXRef = useRef<number | null>(null);

  const testimonialCount = copy.testimonials.items.length;
  const modeCount = copy.modes.items.length;
  const activeMode = copy.modes.items[activeModeIndex] ?? copy.modes.items[0];
  const activeVisual = MODE_VISUALS[language][activeMode.id];
  useEffect(() => {
    debugLandingGa4(
      "[landing][ga4-debug] cookie consent read on load",
      initialCookieConsentStateRef.current,
    );
  }, []);

  useEffect(() => {
    const resolvedLanguage = resolveAuthLanguage(location.search);
    setLanguage(resolvedLanguage);
    syncLocaleLanguage(resolvedLanguage);
  }, [location.search, syncLocaleLanguage]);

  useLandingAnalytics({
    consent: analyticsConsent,
    pathname: location.pathname,
    search: location.search,
  });

  useMetricoolTracking({
    consent: analyticsConsent,
    pathname: location.pathname,
  });

  const handleLanguageChange = (nextLanguage: Language) => {
    setLanguage(nextLanguage);
    setManualLanguage(nextLanguage);
  };
  const toggleThemeMode = () => {
    setPreference(themeMode === "dark" ? "light" : "dark");
  };

  useEffect(() => {
    if (paused || testimonialCount <= 1) {
      return;
    }
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % testimonialCount);
    }, 4000);
    return () => window.clearInterval(timer);
  }, [paused, testimonialCount]);

  useEffect(() => {
    const elements = Array.from(
      document.querySelectorAll<HTMLElement>(".reveal-on-scroll"),
    );

    if (!elements.length) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      elements.forEach((element) => {
        element.classList.add("is-visible");
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -10%" },
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const sectionElement = modesSectionRef.current;

    if (!sectionElement) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsModesInView(entry.isIntersecting);
      },
      { threshold: 0.35 },
    );

    observer.observe(sectionElement);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (
      prefersReducedMotion ||
      hasModeInteracted ||
      !isModesInView ||
      modeCount <= 1
    ) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveModeIndex((current) => (current + 1) % modeCount);
    }, 2500);

    return () => window.clearInterval(timer);
  }, [hasModeInteracted, isModesInView, modeCount]);

  const goToSlide = (index: number) => {
    setActiveSlide((index + testimonialCount) % testimonialCount);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goToSlide(activeSlide - 1);
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      goToSlide(activeSlide + 1);
    }
  };

  const selectMode = (index: number) => {
    setActiveModeIndex((index + modeCount) % modeCount);
  };

  const stopModesAutoplay = () => {
    setHasModeInteracted(true);
  };

  const handleModeSelect = (index: number) => {
    stopModesAutoplay();
    selectMode(index);
  };

  const handleModeThumbKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const { key } = event;

    if (key === "Enter" || key === " " || key === "Spacebar") {
      event.preventDefault();
      handleModeSelect(index);
      return;
    }

    if (key === "ArrowUp" || key === "ArrowLeft") {
      event.preventDefault();
      stopModesAutoplay();
      selectMode(index - 1);
      return;
    }

    if (key === "ArrowDown" || key === "ArrowRight") {
      event.preventDefault();
      stopModesAutoplay();
      selectMode(index + 1);
    }
  };

  const handleModeThumbTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    modeThumbTouchStartXRef.current = event.changedTouches[0]?.clientX ?? null;
  };

  const handleModeThumbTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const startX = modeThumbTouchStartXRef.current;
    const endX = event.changedTouches[0]?.clientX;

    if (startX === null || endX === undefined) {
      modeThumbTouchStartXRef.current = null;
      return;
    }

    if (Math.abs(endX - startX) > 18) {
      stopModesAutoplay();
    }

    modeThumbTouchStartXRef.current = null;
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: language,
    mainEntity: copy.faq.items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  const handleAnalyticsConsent = (nextDecision: "accepted" | "rejected") => {
    const nextState = persistCookieConsentState(nextDecision);
    debugLandingGa4("[landing][ga4-debug] cookie consent updated", {
      nextDecision,
      persistedState: nextState,
    });
    setAnalyticsConsent(nextState.analytics);
    setIsCookiePanelOpen(false);
  };

  return (
    <div
      ref={landingRootRef}
      className={[
        "landing",
        isNarrativeVariant ? "landing--v2-narrative" : "",
        isV3Conversion ? "landing--v3-conversion" : "",
      ].filter(Boolean).join(" ")}
      style={landingStyle}
      data-theme-mode={themeMode}
    >
      <div className="landing-background-layer" aria-hidden="true" />
      <header className="nav">
        <Link
          className="brand"
          to={buildLocalizedAuthPath("/", language)}
          aria-label={
            language === "es" ? "Innerbloom — inicio" : "Innerbloom — home"
          }
        >
          <img
            src="/innerbloom-flower-logo.png"
            alt=""
            className="logo-mark"
            width={50}
            height={50}
          />
          <span className="brand-text">Innerbloom</span>
        </Link>
        {visibleNavLinks.length > 0 ? (
          <nav className="nav-links">
            {visibleNavLinks.map((link) => (
              <a key={link.href} href={link.href}>
                {link.label}
              </a>
            ))}
          </nav>
        ) : null}
        <div className="nav-actions">
          <button
            type="button"
            onClick={toggleThemeMode}
            className="landing-theme-toggle"
            data-analytics-event="landing_theme_toggled"
            data-analytics-value={themeMode === "dark" ? "light" : "dark"}
            aria-label={
              themeMode === "dark"
                ? language === "es"
                  ? "Cambiar a modo claro"
                  : "Switch to light mode"
                : language === "es"
                  ? "Cambiar a modo oscuro"
                  : "Switch to dark mode"
            }
          >
            {themeMode === "dark" ? (
              <svg
                aria-hidden="true"
                className="h-[15px] w-[15px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 1 0 9.8 9.8Z" />
              </svg>
            ) : (
              <svg
                aria-hidden="true"
                className="h-[15px] w-[15px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2.2M12 19.8V22M4.93 4.93l1.56 1.56M17.51 17.51l1.56 1.56M2 12h2.2M19.8 12H22M4.93 19.07l1.56-1.56M17.51 6.49l1.56-1.56" />
              </svg>
            )}
          </button>
          <LanguageDropdown value={language} onChange={handleLanguageChange} />
          {isSignedIn ? (
            <Link className={buttonClasses()} to={dashboardHref}>
              {copy.auth.dashboard}
            </Link>
          ) : (
            <>
              <Link
                className={`${buttonClasses("ghost")} nav-auth-button`}
                data-analytics-cta="login"
                data-analytics-location="nav"
                to={loginHref}
              >
                {copy.auth.login}
              </Link>
              <Link
                className={`${buttonClasses()} nav-auth-button`}
                data-analytics-cta="create_account"
                data-analytics-location="nav"
                to={signUpHref}
              >
                {copy.auth.signup}
              </Link>
            </>
          )}
        </div>
      </header>

      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
        <section className="hero reveal-on-scroll" id="overview">
          <div className="hero-grid">
            <div className="hero-copy">
              <h1>
                {copy.hero.titleLead}{" "}
                <span className="grad">{copy.hero.titleHighlight}</span>
              </h1>
              <p className="sub">{copy.hero.subtitle}</p>
              <div
                className={`mt-6 flex flex-wrap items-center justify-center gap-3 hero-actions ${
                  isSignedIn ? "hero-actions--single" : ""
                }`}
              >
                {isSignedIn ? (
                  <Link className={buttonClasses()} to={dashboardHref}>
                    {copy.auth.dashboard}
                  </Link>
                ) : (
                  <>
                    <Link
                      className={buttonClasses()}
                      data-analytics-cta="start_journey"
                      data-analytics-location="hero"
                      to={onboardingHref}
                    >
                      {copy.auth.startJourney}
                    </Link>
                    {!isV3Conversion ? (
                      <Link
                        className="hero-demo-cta"
                        data-analytics-cta="guided_demo"
                        data-analytics-location="hero"
                        to={buildDemoModeSelectUrl({
                          language,
                          source: "landing",
                        })}
                      >
                        <span>{copy.auth.guidedDemo}</span>
                      </Link>
                    ) : null}
                  </>
                )}
              </div>
              <p className="tiny hero-cta-note">{copy.hero.note}</p>
            </div>
            <div className="hero-media" aria-label={copy.hero.alt}>
              {isV3Conversion ? (
                <HeroProductScene language={language} />
              ) : (
                <HeroPhoneShowcase variant="default" />
              )}
            </div>
          </div>
        </section>

        <section
          className={`truth-problem section-pad reveal-on-scroll ${isOfficialDefault ? "truth-problem--orb-layout" : ""}`}
          id="why"
        >
          <div className="container narrow truth-problem-section">
            {isNarrativeVariant || isOfficialDefault ? (
              <>
                {isV3Conversion ? (
                  <p className="section-kicker">
                    {language === "es" ? "EL PROBLEMA PRINCIPAL" : "THE MAIN PROBLEM"}
                  </p>
                ) : null}
                <AdaptiveText
                  as="h2"
                  className="truth-problem-title truth-problem-title--outside"
                >
                  {copy.problem.title}
                </AdaptiveText>

                <div className={isV3Conversion ? "truth-problem-v2-stage truth-problem-adaptive-stage" : "truth-problem-v2-stage"}>
                  <div className="truth-problem-block truth-problem-block--left">
                    <span className="truth-problem-icon truth-problem-icon--x" aria-hidden>
                      ×
                    </span>
                    <div className="truth-problem-copy">
                      <AdaptiveText as="p" className="truth-problem-primary">
                        {renderMultilineText(copy.problem.leftPrimary)}
                      </AdaptiveText>
                      <AdaptiveText as="p" className="truth-problem-secondary">
                        {renderMultilineText(copy.problem.leftSecondary)}
                      </AdaptiveText>
                    </div>
                  </div>
                  {isV3Conversion ? (
                    <div className="truth-problem-adaptive-graphic">
                      <AdaptiveJourneyGraphic language={language} />
                    </div>
                  ) : (
                    <WeatherCycleOrb />
                  )}
                  <div className="truth-problem-block truth-problem-block--right">
                    <span className="truth-problem-icon truth-problem-icon--check" aria-hidden>
                      ✓
                    </span>
                    <div className="truth-problem-copy">
                      <AdaptiveText as="p" className="truth-problem-primary">
                        {renderMultilineText(copy.problem.rightPrimary)}
                      </AdaptiveText>
                      <AdaptiveText as="p" className="truth-problem-secondary">
                        {renderMultilineText(copy.problem.rightSecondary)}
                      </AdaptiveText>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="truth-problem-kicker">
                  {language === "es" ? "EL PROBLEMA REAL" : "THE REAL PROBLEM"}
                </p>

                <div className="truth-problem-heading-wrap">
                  <AdaptiveText
                    as="h2"
                    className="truth-problem-title truth-problem-title--outside"
                  >
                    {copy.problem.title}
                  </AdaptiveText>
                  <WeatherCycleOrb />
                </div>

                <div className="truth-problem-body">
                  <div className="truth-problem-block truth-problem-block--left">
                    <span className="truth-problem-icon truth-problem-icon--x" aria-hidden>
                      ×
                    </span>
                    <div className="truth-problem-copy">
                      <AdaptiveText as="p" className="truth-problem-primary">
                        {renderMultilineText(copy.problem.leftPrimary)}
                      </AdaptiveText>
                      <AdaptiveText as="p" className="truth-problem-secondary">
                        {renderMultilineText(copy.problem.leftSecondary)}
                      </AdaptiveText>
                    </div>
                  </div>
                  <div className="truth-problem-divider" aria-hidden />
                  <div className="truth-problem-block truth-problem-block--right">
                    <span className="truth-problem-icon truth-problem-icon--check" aria-hidden>
                      ✓
                    </span>
                    <div className="truth-problem-copy">
                      <AdaptiveText as="p" className="truth-problem-primary">
                        {renderMultilineText(copy.problem.rightPrimary)}
                      </AdaptiveText>
                      <AdaptiveText as="p" className="truth-problem-secondary">
                        {renderMultilineText(copy.problem.rightSecondary)}
                      </AdaptiveText>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        <section className="how section-pad reveal-on-scroll" id="how">
          <div className="container narrow">
            {isV3Conversion ? (
              <LandingV3ConversionMethod how={copy.how} language={language} />
            ) : isV2Narrative ? (
              <LandingNarrativeMethod how={copy.how} visuals={V2_METHOD_VISUALS} />
            ) : (
              <>
                <div className="how-heading">
                  <p className="how-kicker">{copy.how.kicker}</p>
                  <AdaptiveText as="h2">{copy.how.title}</AdaptiveText>
                  <AdaptiveText as="p" className="section-sub how-intro">
                    {copy.how.intro}
                  </AdaptiveText>
                </div>
                <PremiumTimeline
                  steps={copy.how.steps}
                  closingLine={copy.how.closingLine}
                  closingBody={copy.how.closingBody}
                  className="mt-2"
                />
              </>
            )}
          </div>
        </section>

        {isV3Conversion ? (
          <LandingV3OnboardingCta
            copy={copy}
            dashboardHref={dashboardHref}
            href={onboardingHref}
            isSignedIn={isSignedIn}
          />
        ) : (
          <section
            className="feature-showcase section-pad reveal-on-scroll"
            id="demo"
          >
          <div className="container narrow">
            <div
              className={`visible-progress-top ${isNarrativeVariant || isOfficialDefault ? "visible-progress-top--v2-text-only" : ""}`}
            >
              <div className="visible-progress-copy">
                {isV3Conversion ? <p className="section-kicker">DEMO</p> : null}
                <AdaptiveText as="h2" className="demo-title">
                  {copy.demo.title}
                </AdaptiveText>
                <AdaptiveText as="p" className="demo-sub">
                  {renderMultilineText(copy.demo.text)}
                </AdaptiveText>
              </div>

              {!isNarrativeVariant && !isOfficialDefault ? (
                <div className="visible-progress-module" aria-hidden>
                <div className="visible-progress-viewport">
                  <div className="visible-progress-scene">
                    <div className="visible-scene-region visible-scene-region--balance visible-scene-fragment visible-scene-fragment--radar">
                      <div className="visible-canvas-header">
                        <p className="visible-canvas-title">BALANCE</p>
                        <span className="visible-canvas-chip">
                          Predominio Body
                        </span>
                      </div>
                      <div className="visible-radar-shell">
                        <div className="visible-balance-radar-wrap">
                          <svg
                            className="visible-balance-radar"
                            viewBox="0 0 420 420"
                            aria-hidden
                          >
                            <defs>
                              <radialGradient
                                id="visible-balance-radar-shape-fill"
                                cx="42%"
                                cy="32%"
                                r="72%"
                              >
                                <stop
                                  offset="0%"
                                  stopColor="rgba(243, 247, 255, 0.52)"
                                />
                                <stop
                                  offset="100%"
                                  stopColor="rgba(189, 205, 243, 0.18)"
                                />
                              </radialGradient>
                              <radialGradient
                                id="visible-balance-radar-inner-fill"
                                cx="50%"
                                cy="42%"
                                r="68%"
                              >
                                <stop
                                  offset="0%"
                                  stopColor="rgba(226, 236, 255, 0.3)"
                                />
                                <stop
                                  offset="100%"
                                  stopColor="rgba(179, 197, 240, 0.08)"
                                />
                              </radialGradient>
                            </defs>
                            <circle
                              className="visible-balance-radar-glow"
                              cx="210"
                              cy="210"
                              r="186"
                            />
                            <polygon
                              className="visible-balance-radar-ring"
                              points="210.0,40.0 295.0,62.8 357.2,125.0 380.0,210.0 357.2,295.0 295.0,357.2 210.0,380.0 125.0,357.2 62.8,295.0 40.0,210.0 62.8,125.0 125.0,62.8"
                            />
                            <polygon
                              className="visible-balance-radar-ring"
                              points="210.0,80.0 275.0,97.4 322.6,145.0 340.0,210.0 322.6,275.0 275.0,322.6 210.0,340.0 145.0,322.6 97.4,275.0 80.0,210.0 97.4,145.0 145.0,97.4"
                            />
                            <polygon
                              className="visible-balance-radar-ring"
                              points="210.0,118.0 256.0,130.3 289.7,164.0 302.0,210.0 289.7,256.0 256.0,289.7 210.0,302.0 164.0,289.7 130.3,256.0 118.0,210.0 130.3,164.0 164.0,130.3"
                            />
                            <polygon
                              className="visible-balance-radar-ring"
                              points="210.0,154.0 238.0,161.5 258.5,182.0 266.0,210.0 258.5,238.0 238.0,258.5 210.0,266.0 182.0,258.5 161.5,238.0 154.0,210.0 161.5,182.0 182.0,161.5"
                            />
                            <line
                              className="visible-balance-radar-axis"
                              x1="210"
                              y1="40"
                              x2="210"
                              y2="380"
                            />
                            <line
                              className="visible-balance-radar-axis"
                              x1="295"
                              y1="62.8"
                              x2="125"
                              y2="357.2"
                            />
                            <line
                              className="visible-balance-radar-axis"
                              x1="357.2"
                              y1="125"
                              x2="62.8"
                              y2="295"
                            />
                            <line
                              className="visible-balance-radar-axis"
                              x1="380"
                              y1="210"
                              x2="40"
                              y2="210"
                            />
                            <line
                              className="visible-balance-radar-axis"
                              x1="357.2"
                              y1="295"
                              x2="62.8"
                              y2="125"
                            />
                            <line
                              className="visible-balance-radar-axis"
                              x1="295"
                              y1="357.2"
                              x2="125"
                              y2="62.8"
                            />
                            <path
                              className="visible-balance-radar-pillar visible-balance-radar-pillar--soul"
                              d="M 323.1 344.8 A 176 176 0 0 1 50.5 284.4"
                            />
                            <path
                              className="visible-balance-radar-pillar visible-balance-radar-pillar--mind"
                              d="M 50.5 284.4 A 176 176 0 0 1 104.1 69.4"
                            />
                            <path
                              className="visible-balance-radar-pillar visible-balance-radar-pillar--body"
                              d="M 104.1 69.4 A 176 176 0 0 1 323.1 344.8"
                            />
                            <polygon
                              className="visible-balance-radar-shape visible-balance-radar-shape--outer"
                              points="210.0,144.3 264.4,115.8 321.9,145.4 309.7,210.0 284.6,253.1 280.8,332.7 210.0,280.3 170.3,278.7 159.0,239.5 40.0,210.0 133.4,165.8 157.9,119.7"
                            />
                            <polygon
                              className="visible-balance-radar-shape visible-balance-radar-shape--inner"
                              points="210.0,159.7 251.6,137.9 295.6,160.6 286.3,210.0 267.0,242.9 264.2,303.8 210.0,263.7 179.7,262.5 171.0,232.5 80.0,210.0 151.5,176.2 170.1,140.9"
                            />
                            <circle
                              className="visible-balance-radar-core"
                              cx="210"
                              cy="210"
                              r="9"
                            />
                            <text
                              className="visible-balance-radar-value"
                              x="28"
                              y="208"
                            >
                              777
                            </text>
                            <text
                              className="visible-balance-radar-value"
                              x="252"
                              y="84"
                            >
                              483
                            </text>
                            <text
                              className="visible-balance-radar-value"
                              x="286"
                              y="336"
                            >
                              517
                            </text>
                            <text
                              className="visible-balance-radar-value"
                              x="132"
                              y="236"
                            >
                              251
                            </text>
                            <text
                              className="visible-balance-radar-value"
                              x="141"
                              y="122"
                            >
                              456
                            </text>
                            <text
                              className="visible-balance-radar-value"
                              x="270"
                              y="158"
                            >
                              414
                            </text>
                            <text
                              className="visible-balance-radar-value"
                              x="288"
                              y="192"
                            >
                              378
                            </text>
                            <text
                              className="visible-balance-radar-value"
                              x="118"
                              y="172"
                            >
                              252
                            </text>
                          </svg>
                        </div>
                      </div>
                      <div className="visible-radar-pillars">
                        <span>ALMA 32%</span>
                        <span>CUERPO 45%</span>
                        <span>MENTE 23%</span>
                      </div>
                    </div>

                    <div className="visible-scene-region visible-scene-region--emotion visible-scene-fragment visible-scene-fragment--emotion">
                      <div className="visible-canvas-header visible-canvas-header--emotion">
                        <p className="visible-canvas-title">EMOTION CHART</p>
                        <span className="visible-canvas-info">i</span>
                      </div>
                      <div className="visible-emotion-legend">
                        <span>Calma</span>
                        <span className="visible-emotion-pill">Felicidad</span>
                        <span>Foco</span>
                      </div>
                      <div className="visible-emotion-grid-months">
                        <span>FEB</span>
                        <span>MAR</span>
                        <span>ABR</span>
                        <span>MAY</span>
                      </div>
                      <div className="visible-emotion-grid">
                        {EMOTION_HEATMAP_ROWS.map((row, rowIndex) => (
                          <div
                            className="visible-emotion-grid-row"
                            key={`emotion-row-${rowIndex}`}
                          >
                            {row.map((emotion, cellIndex) => (
                              <span
                                key={`emotion-cell-${rowIndex}-${cellIndex}`}
                                className={`visible-emotion-cell visible-emotion-cell--${emotion}`}
                                style={
                                  {
                                    "--emotion-order":
                                      rowIndex * 19 + cellIndex,
                                  } as CSSProperties
                                }
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="visible-scene-inner-contours" aria-hidden>
                      <span className="visible-inner-contour visible-inner-contour--emotion" />
                    </div>

                    <div className="visible-scene-region visible-scene-region--streaks visible-scene-fragment visible-scene-fragment--streaks">
                      <div className="visible-canvas-header visible-canvas-header--streaks">
                        <p className="visible-canvas-title">STREAKS</p>
                        <span className="visible-canvas-info">i</span>
                        <span className="visible-canvas-chip visible-canvas-chip--flow">
                          FLOW · 3M
                        </span>
                      </div>
                      <div
                        className="visible-streak-pillars"
                        role="tablist"
                        aria-label="Pilares"
                      >
                        <span className="is-active">🫀 BODY</span>
                        <span>🧠 MIND</span>
                        <span>🏵️ SOUL</span>
                      </div>
                      <p className="visible-streak-heading">Top streaks</p>
                      <div className="visible-streak-row">
                        <div className="visible-streak-row-head">
                          <p>Minoxidil noche</p>
                          <span>5/3</span>
                        </div>
                        <p className="visible-streak-row-meta">Recuperación</p>
                        <div className="visible-streak-bar">
                          <span className="visible-streak-fill visible-streak-fill--first" />
                        </div>
                      </div>
                      <div className="visible-streak-row">
                        <div className="visible-streak-row-head">
                          <p>Ayuno hasta las 14hs</p>
                          <span>5/3</span>
                        </div>
                        <p className="visible-streak-row-meta">Nutrición</p>
                        <div className="visible-streak-bar">
                          <span className="visible-streak-fill visible-streak-fill--second" />
                        </div>
                      </div>
                      <div className="visible-streak-row">
                        <div className="visible-streak-row-head">
                          <p>Dormir 8hs</p>
                          <span>4/3</span>
                        </div>
                        <p className="visible-streak-row-meta">Sueño</p>
                        <div className="visible-streak-bar">
                          <span className="visible-streak-fill visible-streak-fill--third" />
                        </div>
                      </div>
                      <div className="visible-streak-periods">
                        <span>SEM</span>
                        <span className="is-active">MES</span>
                        <span>3M</span>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              ) : null}
            </div>

            <div className="demo-bridge">
              <p className="demo-bridge-copy">{copy.demo.banner}</p>
              <div className="demo-actions">
                <Link
                  className={buttonClasses()}
                  data-analytics-cta="guided_demo"
                  data-analytics-location="feature"
                  to={buildDemoModeSelectUrl({ language, source: "landing" })}
                >
                  {copy.demo.cta}
                </Link>
              </div>
            </div>
          </div>
          </section>
        )}

        {isV3Conversion ? null : (
          <>
        <section className="why section-pad reveal-on-scroll" id="pillars">
          <div className="container narrow">
            <p className="section-kicker">{copy.pillars.kicker}</p>
            <AdaptiveText as="h2" className="pillars-title">
              {copy.pillars.title}
            </AdaptiveText>
            <AdaptiveText as="p" className="section-sub pillars-intro">
              {copy.pillars.intro}
            </AdaptiveText>
            <div className="cards grid-3">
              {copy.pillars.items.map((pillar, index) => {
                const { definition, examples } = splitPillarCopy(
                  pillar.copy,
                  language,
                );
                return (
                  <article
                    className="card card--hero-glass pillar-card fade-item"
                    key={pillar.title}
                    style={{ "--delay": `${index * 90}ms` } as CSSProperties}
                  >
                    <h3 className="pillar-heading">
                      <span className="pillar-emoji" aria-hidden>
                        {pillar.emoji}
                      </span>
                      <span className="concept-term concept-term--pillar">
                        {pillar.title}
                      </span>
                    </h3>
                    <p className="pillar-definition">{definition}</p>
                    {examples.length > 0 ? (
                      <div
                        className="pillar-examples"
                        aria-label={PILLAR_EXAMPLES_LABEL[language]}
                      >
                        <span className="pillar-examples-label">
                          {PILLAR_EXAMPLES_LABEL[language]}
                        </span>
                        <div className="pillar-chips" role="list">
                          {examples.map((example) => (
                            <span
                              key={example}
                              className="pillar-chip"
                              role="listitem"
                            >
                              {example}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
            <AdaptiveText as="p" className="section-sub highlight">
              {copy.pillars.highlight}
            </AdaptiveText>
          </div>
        </section>

        <section className="section-pad reveal-on-scroll" id="rhythms">
          <div className="container">
            <LabsWeeklyRhythmSystemSection
              language={language}
              headingAlignment="center"
            />
          </div>
        </section>

        <section
          ref={modesSectionRef}
          className="modes section-pad reveal-on-scroll"
          id="modes"
        >
          <div className="container">
            <p className="section-kicker">{copy.modes.kicker}</p>
            <AdaptiveText as="h2" className="modes-title">
              {copy.modes.title}
            </AdaptiveText>
            <AdaptiveText as="p" className="section-sub modes-intro">
              {copy.modes.intro}
            </AdaptiveText>
            <div
              className="modes-carousel"
              aria-live="polite"
              style={
                { "--mode-count": copy.modes.items.length } as CSSProperties
              }
            >
              <div
                className="mode-thumbs"
                role="listbox"
                aria-label={
                  language === "es" ? "Elegir avatar" : "Choose avatar"
                }
                onTouchStart={handleModeThumbTouchStart}
                onTouchEnd={handleModeThumbTouchEnd}
              >
                {copy.modes.items.map((mode, index) => {
                  const visual = MODE_VISUALS[language][mode.id];
                  const isActive = index === activeModeIndex;
                  return (
                    <button
                      key={mode.id}
                      type="button"
                      role="option"
                      aria-label={mode.title}
                      aria-selected={isActive}
                      className={`mode-thumb ${isActive ? "is-active" : ""}`}
                      onClick={() => handleModeSelect(index)}
                      onKeyDown={(event) =>
                        handleModeThumbKeyDown(event, index)
                      }
                    >
                      <img src={visual.thumbImage} alt="" aria-hidden />
                    </button>
                  );
                })}
              </div>

              <article
                className={`card card--hero-glass mode mode-main mode-${activeMode.id} fade-item`}
              >
                <header className="mode-header">
                  <div className="mode-title">{activeMode.title}</div>
                </header>
                <figure className="mode-media">
                  <video
                    className="mode-video"
                    src={activeVisual.avatarVideo}
                    poster={activeVisual.avatarImage}
                    autoPlay
                    muted
                    loop
                    playsInline
                    aria-label={activeVisual.avatarAlt}
                  />
                  <figcaption className="mode-media-caption">
                    {activeVisual.avatarLabel}
                  </figcaption>
                </figure>
                <p className="mode-avatar-copy">{activeMode.goal}</p>
              </article>
            </div>
          </div>
        </section>

          </>
        )}

        <section
          className={`testimonials section-pad reveal-on-scroll ${isV3Conversion ? "testimonials--v3-quiet" : ""}`}
          id="testimonials"
        >
          <div className="container">
            <AdaptiveText as="h2">{copy.testimonials.title}</AdaptiveText>
            <AdaptiveText as="p" className="section-sub">
              {copy.testimonials.intro}
            </AdaptiveText>
            {isV3Conversion ? (
              <div
                className="v3-review-carousel"
                aria-roledescription="carousel"
                tabIndex={0}
                onKeyDown={handleKeyDown}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                onFocus={() => setPaused(true)}
                onBlur={() => setPaused(false)}
              >
                <div
                  className="v3-review-track"
                  style={{ transform: `translateX(-${activeSlide * 100}%)` }}
                >
                  {copy.testimonials.items.map((testimonial, index) => (
                    <figure
                      className="v3-review-card"
                      key={`${testimonial.author}-${index}`}
                      role="group"
                      id={`slide-${index + 1}`}
                      aria-label={
                        language === "es"
                          ? `${index + 1} de ${testimonialCount}`
                          : `${index + 1} of ${testimonialCount}`
                      }
                    >
                      <div className="v3-review-stars" aria-label="5 stars">
                        <span>★★★★★</span>
                      </div>
                      <blockquote>{testimonial.quote}</blockquote>
                      <figcaption>{testimonial.author}</figcaption>
                    </figure>
                  ))}
                </div>
                <div className="v3-review-controls">
                  <button
                    type="button"
                    className="v3-review-arrow"
                    aria-label={copy.testimonials.prev}
                    onClick={() => goToSlide(activeSlide - 1)}
                  >
                    ‹
                  </button>
                  <div
                    className="v3-review-dots"
                    role="tablist"
                    aria-label={copy.testimonials.groupLabel}
                  >
                    {copy.testimonials.items.map((testimonial, index) => (
                      <button
                        key={`${testimonial.author}-${index}`}
                        className="v3-review-dot"
                        role="tab"
                        aria-selected={index === activeSlide}
                        aria-controls={`slide-${index + 1}`}
                        onClick={() => goToSlide(index)}
                      >
                        <span className="visually-hidden">{index + 1}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="v3-review-arrow"
                    aria-label={copy.testimonials.next}
                    onClick={() => goToSlide(activeSlide + 1)}
                  >
                    ›
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="slider"
                id="testi-slider"
                aria-roledescription="carousel"
                tabIndex={0}
                onKeyDown={handleKeyDown}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                onFocus={() => setPaused(true)}
                onBlur={() => setPaused(false)}
              >
                <div
                  className="slider-track"
                  style={{ transform: `translateX(-${activeSlide * 100}%)` }}
                >
                  {copy.testimonials.items.map((testimonial, index) => (
                    <figure
                      className="testi"
                      key={`${testimonial.author}-${index}`}
                      role="group"
                      id={`slide-${index + 1}`}
                      aria-label={
                        language === "es"
                          ? `${index + 1} de ${testimonialCount}`
                          : `${index + 1} of ${testimonialCount}`
                      }
                    >
                      <blockquote>{testimonial.quote}</blockquote>
                      <figcaption>{testimonial.author}</figcaption>
                    </figure>
                  ))}
                </div>
                <button
                  type="button"
                  className="slider-btn prev"
                  aria-label={copy.testimonials.prev}
                  onClick={() => goToSlide(activeSlide - 1)}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="slider-btn next"
                  aria-label={copy.testimonials.next}
                  onClick={() => goToSlide(activeSlide + 1)}
                >
                  ›
                </button>
                <div
                  className="slider-dots"
                  role="tablist"
                  aria-label={copy.testimonials.groupLabel}
                >
                  {copy.testimonials.items.map((testimonial, index) => (
                    <button
                      key={`${testimonial.author}-${index}`}
                      className="dot"
                      role="tab"
                      aria-selected={index === activeSlide}
                      aria-controls={`slide-${index + 1}`}
                      onClick={() => goToSlide(index)}
                    >
                      <span className="visually-hidden">{index + 1}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <section className={`faq section-pad reveal-on-scroll ${isV3Conversion ? "faq--v3-quiet" : ""}`} id="faq">
          <div className="container narrow">
            <AdaptiveText as="h2">{copy.faq.title}</AdaptiveText>
            {copy.faq.items.map((faq) => (
              <details key={faq.question}>
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {SHOW_LANDING_PRICING ? (
          <section
            className="pricing section-pad reveal-on-scroll"
            id="pricing"
          >
            <div className="container">
              <AdaptiveText as="h2">{copy.pricing.title}</AdaptiveText>
              <AdaptiveText as="p" className="section-sub">
                {copy.pricing.intro}
              </AdaptiveText>
              <p className="pricing-trial-highlight">
                {copy.pricing.trialHighlight}
              </p>
              <p className="pricing-tax-note">{copy.pricing.taxNote}</p>
              <div className="pricing-grid">
                {copy.pricing.plans.map((plan, index) => (
                  <article
                    className="card pricing-card fade-item"
                    key={plan.id}
                    style={{ "--delay": `${index * 90}ms` } as CSSProperties}
                  >
                    {plan.id === "YEAR" ? (
                      <span className="pricing-best-deal-chip">best deal</span>
                    ) : null}
                    <p className="pricing-plan-name">{plan.name}</p>
                    <p className="pricing-plan-detail">{plan.detail}</p>
                    <p className="pricing-plan-price">{plan.price}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        <section className={`next section-pad reveal-on-scroll ${isV3Conversion ? "next--v3-final" : ""}`}>
          <div className="container narrow center">
            <AdaptiveText as="h2">{copy.next.title}</AdaptiveText>
            <AdaptiveText as="p" className="section-sub">
              {copy.next.intro}
            </AdaptiveText>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              {isSignedIn ? (
                <Link className={buttonClasses()} to={dashboardHref}>
                  {copy.auth.dashboard}
                </Link>
              ) : (
                <>
                  <Link
                    className={buttonClasses()}
                    data-analytics-cta="start_journey"
                    data-analytics-location="footer"
                    to={onboardingHref}
                  >
                    {copy.auth.startJourney}
                  </Link>
                </>
              )}
            </div>
            <p className="landing-discord-cta">
              {DISCORD_CTA_COPY[language].lead}{" "}
              <a href={DISCORD_URL} target="_blank" rel="noreferrer">
                {DISCORD_CTA_COPY[language].link}
              </a>
            </p>
          </div>
        </section>
      </main>

      <footer className="footer">
        <span>{copy.footer.copyright}</span>
        <nav className="footer-links">
          {isSignedIn ? (
            <Link to={dashboardHref}>Dashboard</Link>
          ) : (
            <>
              <Link
                data-analytics-cta="login"
                data-analytics-location="footer"
                to={loginHref}
              >
                {copy.auth.login}
              </Link>
              <Link
                data-analytics-cta="create_account"
                data-analytics-location="footer"
                to={signUpHref}
              >
                {copy.auth.signup}
              </Link>
            </>
          )}
          <a href="#faq">{copy.footer.faq}</a>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/support">Support</Link>
          <button
            type="button"
            className="footer-cookies-link"
            onClick={() => setIsCookiePanelOpen(true)}
          >
            {language === "es" ? "Cookies" : "Cookies"}
          </button>
          <a
            className="footer-community-link footer-community-link--reddit"
            data-analytics-cta="join_subreddit"
            data-analytics-location="footer"
            href="https://www.reddit.com/r/InnerbloomJourney/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Join our subreddit"
            title="Join our subreddit"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M20.6 13.4a2.4 2.4 0 0 0-3.95-2.66 9.2 9.2 0 0 0-4.2-1.1l.72-3.38 2.36.5a1.8 1.8 0 1 0 .3-1.1l-3.06-.66a.56.56 0 0 0-.66.43l-.86 4.02a9.33 9.33 0 0 0-4.45 1.13 2.4 2.4 0 1 0-1.32 4.4q-.03.3-.03.63c0 3.04 3.05 5.5 6.81 5.5s6.8-2.46 6.8-5.5q0-.3-.03-.58a2.4 2.4 0 0 0 1.57-2.23Zm-11.82 1.2a1.13 1.13 0 1 1 0-2.26 1.13 1.13 0 0 1 0 2.26Zm6.46 3.1c-.8.78-2.34 1.04-3.23 1.04s-2.43-.26-3.23-1.04a.56.56 0 1 1 .78-.8c.47.46 1.56.72 2.45.72s1.98-.26 2.45-.72a.56.56 0 1 1 .78.8Zm-.03-3.1a1.13 1.13 0 1 1 0-2.26 1.13 1.13 0 0 1 0 2.26Z"
                fill="currentColor"
              />
            </svg>
          </a>
          <a
            className="footer-community-link"
            data-analytics-cta="join_discord"
            data-analytics-location="footer"
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="Join us on Discord"
            title="Join us on Discord"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M20.32 4.37a18.3 18.3 0 0 0-4.63-1.47.08.08 0 0 0-.08.04c-.2.36-.42.84-.58 1.22a17 17 0 0 0-5.06 0 12 12 0 0 0-.59-1.22.08.08 0 0 0-.08-.04A18.27 18.27 0 0 0 4.7 4.37a.07.07 0 0 0-.03.03C1.73 8.79.93 13.07 1.32 17.3a.1.1 0 0 0 .04.07 18.4 18.4 0 0 0 5.68 2.9.08.08 0 0 0 .09-.03c.44-.6.84-1.24 1.18-1.91a.08.08 0 0 0-.04-.11 12 12 0 0 1-1.8-.86.08.08 0 0 1-.01-.13q.19-.14.39-.3a.07.07 0 0 1 .08-.01c3.77 1.72 7.85 1.72 11.58 0a.07.07 0 0 1 .08.01q.2.17.39.3a.08.08 0 0 1-.01.13 12 12 0 0 1-1.8.86.08.08 0 0 0-.04.11c.35.67.74 1.3 1.18 1.9a.08.08 0 0 0 .09.04 18.34 18.34 0 0 0 5.69-2.9.08.08 0 0 0 .04-.07c.47-4.89-.79-9.14-3.36-12.9a.06.06 0 0 0-.03-.03M8.02 14.72c-1.14 0-2.08-1.05-2.08-2.34s.92-2.34 2.08-2.34 2.1 1.06 2.08 2.34-.92 2.34-2.08 2.34m7.67 0c-1.14 0-2.08-1.05-2.08-2.34s.92-2.34 2.08-2.34 2.1 1.06 2.08 2.34-.92 2.34-2.08 2.34"
                fill="currentColor"
              />
            </svg>
          </a>
        </nav>
      </footer>
      <BackToTopButton language={language} />
      <CookieConsentBanner
        language={language}
        isOpen={isCookiePanelOpen}
        hasDecision={analyticsConsent !== "unset"}
        onAccept={() => handleAnalyticsConsent("accepted")}
        onReject={() => handleAnalyticsConsent("rejected")}
        onClose={() => setIsCookiePanelOpen(false)}
      />
    </div>
  );
}
